import React from "react";
import { Box, Typography, Divider, Paper, Grid } from "@mui/material";
import { WarningAmberRounded } from "@mui/icons-material";
import { calculateAge } from "../../utility/general";

/**
 * TemplateExecutiveSummary
 * A document-style layout that prioritizes a summary of
 * key findings at the top.
 */

// Helper function to determine if a result is abnormal
const isAbnormalResult = (result, testDetails) => {
  if (!result || !testDetails) return false;

  const resultValue = parseFloat(result);
  if (isNaN(resultValue)) return false;

  // Check all reference ranges in priority order
  const ranges = [
    testDetails.refRange_Adult,
    testDetails.refRange_Children,
    testDetails.refRange_FeMale,
    testDetails.refRange_Male,
    testDetails.refRange_Elderly,
    testDetails.normalRefRange
  ].filter(Boolean);

  if (ranges.length === 0) return false;

  // Parse the range (e.g., "80 - 100" or "70-90")
  const rangeStr = ranges[0];
  const parts = rangeStr.split(/[-–]/).map(p => parseFloat(p.trim()));

  if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return false;

  const [min, max] = parts;
  return resultValue < min || resultValue > max;
};

// Extract all abnormal results from reportData
const getAbnormalResults = (reportData) => {
  const abnormal = [];

  (reportData?.patientTestReports || []).forEach((report) => {
    (report.labTestServiceResults || []).forEach((result) => {
      if (isAbnormalResult(result.result, result.testDetails)) {
        abnormal.push({
          parameter: result.testDetails?.parameter || "Unknown",
          result: result.result,
          unit: result.testDetails?.unitOfMeasurement || "",
          range: (() => {
            if (result.testDetails?.refRange_Adult) return result.testDetails.refRange_Adult;
            if (result.testDetails?.refRange_Children) return result.testDetails.refRange_Children;
            if (result.testDetails?.refRange_FeMale) return result.testDetails.refRange_FeMale;
            if (result.testDetails?.refRange_Male) return result.testDetails.refRange_Male;
            if (result.testDetails?.refRange_Elderly) return result.testDetails.refRange_Elderly;
            return result.testDetails?.normalRefRange || "-";
          })(),
          panel: report.internalLabService?.name || "Unknown Panel"
        });
      }
    });
  });

  return abnormal;
};

export default function TemplateAlt2({ 
    reportData, 
    color, 
    letterheadUrl, 
    labName, 
    labLocation, 
    renderRow 
}) {
  const reports = reportData?.patientTestReports || [];
  const abnormalResults = getAbnormalResults(reportData);

  return (
    <Box sx={{ p: 3, maxWidth: '900px', margin: 'auto' }}>
      
      {/* 1. Header Section */}
      <Box 
        sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "flex-start",
          mb: 2,
          gap: 2,
        }}
      >
        {letterheadUrl && (
          <Box 
            component="img" 
            src={letterheadUrl} 
            alt="letterhead" 
            sx={{ width: 80, height: 80, objectFit: "contain" }} 
            data-letterhead="true" 
          />
        )}
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          <Typography variant="h5" sx={{ color, fontWeight: 800 }}>
            {labName}
          </Typography>
          {labLocation && (
            <Typography variant="body2" sx={{ color: "#666" }}>
              {labLocation}
            </Typography>
          )}
        </Box>
      </Box>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
        Laboratory Report
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {/* 2. Patient & Report Info Block */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="overline" sx={{ color: '#555' }}>Patient</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {reportData?.patientFullName || (reportData?.walkInPatient?.firstName + ' ' + reportData?.walkInPatient?.lastName) || "N/A"}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="overline" sx={{ color: '#555' }}>Age</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {calculateAge(reportData?.walkInPatient?.dateOfBirth)}
          </Typography>
        </Grid>
         <Grid item xs={12} md={6}>
          <Typography variant="overline" sx={{ color: '#555' }}>Gender</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {reportData.gender || reportData?.walkInPatient?.gender  || "N/A"}
          </Typography>
        </Grid>
      </Grid>

      {/* 3. Report Summary Box (Dynamic Abnormal Results) */}
      {abnormalResults.length > 0 && (
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 3, 
            p: 2,
            background: '#fff8e1',
            border: '1px solid #ffecb3',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <WarningAmberRounded sx={{ color: '#f57f17', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#f57f17' }}>
              Report Summary — {abnormalResults.length} Abnormal Result{abnormalResults.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            The following results were identified outside of the standard reference range.
            Please review the detailed results below.
          </Typography>
          {abnormalResults.map((item, idx) => (
            <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: idx < abnormalResults.length - 1 ? '1px solid #ffe0b2' : 'none' }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.parameter}</Typography>
                <Typography variant="caption" sx={{ color: '#999' }}>{item.panel}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                  {item.result} {item.unit}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Ref: {item.range}
                </Typography>
              </Box>
            </Box>
          ))}
        </Paper>
      )}

      {/* 4. Detailed Results Section */}
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#333', mb: 2 }}>
        Detailed Results
      </Typography>
      
      {reports.map((r, index) => (
        <Box key={r.id} sx={{ mb: 2 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700, 
              color: color,
              mb: 1.5,
              borderBottom: `2px solid ${color}`,
              pb: 0.5
            }}
          >
            {r.internalLabService?.name}
          </Typography>
          
          {/* Your renderRow function is placed here */}
          <Box>
            {renderRow(r)}
          </Box>
          
          {index < reports.length - 1 && <Divider sx={{ mt: 3 }} />}
        </Box>
      ))}
    </Box>
  );
}